import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

export function useCarFilterSidebar(props, emit) {
  // Helper methods for additional attributes
  function toggleAdditionalAttribute(attributeId, checked) {
    if (checked) {
      // For boolean attributes, set to true
      props.filters.additionalAttributes[attributeId] = true;
    } else {
      // Remove the attribute if unchecked
      delete props.filters.additionalAttributes[attributeId];
    }
    // Emit the updated filters
    emit('update-filters', props.filters);
  }

  function toggleAttributeOption(attributeId, optionId, checked) {
    // Initialize the attribute array if it doesn't exist
    if (!props.filters.additionalAttributes[attributeId]) {
      props.filters.additionalAttributes[attributeId] = [];
    }

    if (checked) {
      // Add the option to the array if it's not already there
      if (!props.filters.additionalAttributes[attributeId].includes(optionId)) {
        props.filters.additionalAttributes[attributeId].push(optionId);
      }
    } else {
      // Remove the option from the array
      if (Array.isArray(props.filters.additionalAttributes[attributeId])) {
        props.filters.additionalAttributes[attributeId] = props.filters.additionalAttributes[attributeId].filter(id => id !== optionId);

        // If the array is empty, remove the attribute
        if (props.filters.additionalAttributes[attributeId].length === 0) {
          delete props.filters.additionalAttributes[attributeId];
        }
      }
    }

    // Emit the updated filters
    emit('update-filters', props.filters);
  }

  function isOptionSelected(attributeId, optionId) {
    return Array.isArray(props.filters.additionalAttributes[attributeId]) &&
           props.filters.additionalAttributes[attributeId].includes(optionId);
  }

  // Reference to the additional attributes modal
  const additionalAttributesModal = ref(null);

  // State for Volvo Selekt modal
  const isModalVisible = ref(false);

  // Show the additional attributes modal
  function showAdditionalAttributesModal() {
    additionalAttributesModal.value.show();
  }

  // Show Volvo Select tooltip
  function showVolvoSelectTooltip(event) {
    // Check if we're on mobile
    if (window.innerWidth <= 768) {
      // Show modal on mobile
      isModalVisible.value = true;
    } else {
      // Show tooltip on desktop
      // Get the trigger element
      const trigger = event.currentTarget;

      // Close any other active tooltips
      document.querySelectorAll('.volvo-selekt-tooltip-trigger.active').forEach((activeTrigger) => {
        if (activeTrigger !== trigger) {
          activeTrigger.classList.remove('active');
        }
      });

      // Toggle the current one
      trigger.classList.toggle('active');
    }

    // Prevent the event from propagating to the document click handler
    event.stopPropagation();
  }

  // Close tooltip when clicking outside
  function handleDocumentClick(e) {
    const activeTriggers = document.querySelectorAll('.volvo-selekt-tooltip-trigger.active');
    if (!Array.from(activeTriggers).some(trigger => trigger.contains(e.target))) {
      activeTriggers.forEach(trigger => trigger.classList.remove('active'));
    }
  }

  // Apply additional attributes
  function applyAdditionalAttributes() {
    emit('update-filters', props.filters);
  }

  // Computed properties for v-model bindings
  const modelSelectedMileageRange = computed({
    get: () => props.selectedMileageRange,
    set: (value) => emit('update:selectedMileageRange', value)
  });

  const modelSelectedAgeRange = computed({
    get: () => props.selectedAgeRange,
    set: (value) => emit('update:selectedAgeRange', value)
  });

  const modelSelectedPriceRange = computed({
    get: () => props.selectedPriceRange,
    set: (value) => emit('update:selectedPriceRange', value)
  });

  // Add and remove document click event listener
  onMounted(() => {
    document.addEventListener('click', handleDocumentClick);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick);
  });

  const showShowrooms = ref(true);
  const showAllModels = ref(false);
  const showAllBodyTypes = ref(false);
  const showAllBodyColors = ref(false);
  const showAllCategories = ref(false);

  // Auto-expand filter sections when any option is selected
  watch(() => props.filters.models, (newModels) => {
    if (newModels && newModels.length > 0) {
      showAllModels.value = true;
    }
  }, { immediate: true });

  watch(() => props.filters.bodyTypes, (newBodyTypes) => {
    if (newBodyTypes && newBodyTypes.length > 0) {
      showAllBodyTypes.value = true;
    }
  }, { immediate: true });

  watch(() => props.filters.bodyColors, (newBodyColors) => {
    if (newBodyColors && newBodyColors.length > 0) {
      showAllBodyColors.value = true;
    }
  }, { immediate: true });

  watch(() => props.filters.categories, (newCategories) => {
    if (newCategories && newCategories.length > 0) {
      showAllCategories.value = true;
    }
  }, { immediate: true });
  const localPriceRange = ref([0, 0]);

  // Price slider logic
  watch(
    () => props.histogram,
    (h) => {
      if (h.min !== h.max && h.histogram.length) {
        // use existing price filters if set, otherwise default to histogram boundaries
        const from = props.filters.priceFrom != null ? props.filters.priceFrom : h.min;
        const to = props.filters.priceTo != null ? props.filters.priceTo : h.max;
        localPriceRange.value = [from, to];
      }
    },
    { immediate: true }
  );

  watch(localPriceRange, ([from, to]) => {
    const min = props.histogram?.min ?? 0;
    const max = props.histogram?.max ?? 0;
    const isFullHistogramRange = from === min && to === max;
    const normalizedFrom = isFullHistogramRange ? null : from;
    const normalizedTo = isFullHistogramRange ? null : to;

    // Only emit when different from current filters to avoid loops
    if (props.filters.priceFrom !== normalizedFrom || props.filters.priceTo !== normalizedTo) {
      emit('update-filters', {
        ...props.filters,
        priceFrom: normalizedFrom,
        priceTo: normalizedTo
      });
    }
  }, { deep: true });

  // Keep slider in sync when selectedPriceRange changes (e.g., from URL on mount)
  watch(() => props.selectedPriceRange, (range) => {
    if (!range) return;
    if (range === '-') {
      // When radio is "All" or no radio matches, don't override the slider.
      return;
    }
    if (range.startsWith('-')) {
      const to = Number(range.slice(1));
      const from = props.histogram?.min ?? 0;
      localPriceRange.value = [from, to];
      return;
    }
    if (range.endsWith('-')) {
      const from = Number(range.slice(0, -1));
      const to = props.histogram?.max ?? from;
      localPriceRange.value = [from, to];
      return;
    }
    if (range.includes('-')) {
      const [from, to] = range.split('-').map(Number);
      localPriceRange.value = [from, to];
    }
  }, { immediate: true });

  // Keep slider in sync with filters.priceFrom/To (e.g., after URL parsing)
  watch(() => [props.filters.priceFrom, props.filters.priceTo, props.histogram], () => {
    const min = props.histogram?.min ?? 0;
    const max = props.histogram?.max ?? 0;
    const from = props.filters.priceFrom != null ? props.filters.priceFrom : min;
    const to   = props.filters.priceTo   != null ? props.filters.priceTo   : max;
    if (localPriceRange.value[0] !== from || localPriceRange.value[1] !== to) {
      localPriceRange.value = [from, to];
    }
  }, { immediate: true });

  // Mileage slider logic - round maxMileage up to nearest 10k with minimum 300k
  const sliderMaxMileage = computed(() => Math.max(300000, Math.ceil(props.maxMileage / 10000) * 10000));
  const localMileageRange = ref([
    props.filters.mileageFrom ?? 0,
    props.filters.mileageTo ?? Math.max(300000, Math.ceil(props.maxMileage / 10000) * 10000),
  ]);

  // Watch for changes in filters.mileageFrom and filters.mileageTo
  watch(() => [props.filters.mileageFrom, props.filters.mileageTo], ([newFrom, newTo]) => {
    localMileageRange.value = [
      newFrom ?? 0,
      newTo ?? Math.max(300000, Math.ceil(props.maxMileage / 10000) * 10000)
    ];
  }, { immediate: true });

  watch(
    () => props.maxMileage,
    max => {
      const roundedMax = Math.max(300000, Math.ceil(max / 10000) * 10000);
      localMileageRange.value = [
        props.filters.mileageFrom ?? 0,
        props.filters.mileageTo ?? roundedMax,
      ];
    },
    { immediate: true }
  );

  watch(localMileageRange, ([from, to]) => {
    emit('update-filters', { ...props.filters, mileageFrom: from, mileageTo: to });
  }, { deep: true });

  // Watch for selectedMileageRange changes and update localMileageRange accordingly
  watch(() => props.selectedMileageRange, (range) => {
    if (range && range !== '-') {
      if (range.startsWith('-')) {
        // Range like "-50000" means from 0 to 50000 km
        const mileage = Number(range.slice(1));
        localMileageRange.value = [0, mileage];
      } else if (range.endsWith('-')) {
        // Range like "100000-" means from 100000 km to max
        const mileage = Number(range.slice(0, -1));
        localMileageRange.value = [mileage, Math.max(300000, Math.ceil(props.maxMileage / 10000) * 10000)];
      } else if (range.includes('-')) {
        // Range like "50000-100000" means from 50k to 100k km
        const [from, to] = range.split('-').map(Number);
        localMileageRange.value = [from, to];
      }
    }
  }, { immediate: true });

  // Age slider logic
  const minYear = 1990;
  const currentYear = new Date().getFullYear();
  const sliderMinYear = computed(() => minYear);
  const sliderMaxYear = computed(() => currentYear);
  const localYearRange = ref([
    props.filters.ageFrom ?? sliderMinYear.value,
    props.filters.ageTo ?? sliderMaxYear.value,
  ]);

  // Watch for changes in filters.ageFrom and filters.ageTo
  watch(() => [props.filters.ageFrom, props.filters.ageTo], ([newFrom, newTo]) => {
    localYearRange.value = [
      newFrom ?? sliderMinYear.value,
      newTo ?? sliderMaxYear.value
    ];
  }, { immediate: true });

  // Only update the min/max if they're not already set
  watch(sliderMinYear, newMin => {
    if (props.filters.ageFrom === null) {
      localYearRange.value[0] = newMin;
    }
  }, { immediate: true });

  watch(sliderMaxYear, newMax => {
    if (props.filters.ageTo === null) {
      localYearRange.value[1] = newMax;
    }
  }, { immediate: true });

  watch(localYearRange, ([from, to]) => {
    emit('update-filters', { ...props.filters, ageFrom: from, ageTo: to });
  }, { deep: true });

  // Watch for selectedAgeRange changes and update localYearRange accordingly
  watch(() => props.selectedAgeRange, (range) => {
    if (range && range !== '-') {
      if (range.startsWith('-')) {
        // Range like "-3" means up to 3 years old (from 3 years ago to current year)
        const years = Number(range.slice(1));
        const minYear = currentYear - years;
        localYearRange.value = [minYear, sliderMaxYear.value];
      } else if (range.endsWith('-')) {
        // Range like "8-" means more than 8 years old (from min to 8 years ago)
        const years = Number(range.slice(0, -1));
        const maxYear = currentYear - years;
        localYearRange.value = [sliderMinYear.value, maxYear];
      } else if (range.includes('-')) {
        // Range like "3-8" means from 3 to 8 years ago
        const [from, to] = range.split('-').map(Number);
        const minYear = currentYear - to;
        const maxYear = currentYear - from;
        localYearRange.value = [minYear, maxYear];
      }
    }
  }, { immediate: true });

  // Propagate other watches
  // Emit changes whenever filters object mutates
  watch(
    () => props.filters,
    (newFilters) => emit('update-filters', newFilters),
    { deep: true }
  );
  watch(
    () => props.selectedMileageRange,
    val => emit('update:selectedMileageRange', val)
  );
  watch(
    () => props.selectedAgeRange,
    val => emit('update:selectedAgeRange', val)
  );
  watch(
    () => props.selectedPriceRange,
    val => emit('update:selectedPriceRange', val)
  );

  return {
    toggleAdditionalAttribute,
    toggleAttributeOption,
    isOptionSelected,
    additionalAttributesModal,
    isModalVisible,
    showAdditionalAttributesModal,
    showVolvoSelectTooltip,
    handleDocumentClick,
    applyAdditionalAttributes,
    modelSelectedMileageRange,
    modelSelectedAgeRange,
    modelSelectedPriceRange,
    showShowrooms,
    showAllModels,
    showAllBodyTypes,
    showAllBodyColors,
    showAllCategories,
    localPriceRange,
    sliderMaxMileage,
    localMileageRange,
    minYear,
    currentYear,
    sliderMinYear,
    sliderMaxYear,
    localYearRange
  };
}
